import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Music, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Namaste! I\'m Musicoul Assistant. Ask me anything about music or our courses!' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [listening, setListening] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [lang, setLang] = useState('en');
  const endRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Check stored language preference
  useEffect(() => {
    const stored = localStorage.getItem('musicoul_lang');
    if (stored && stored !== 'en') setLang(stored);
  }, []);

  const speak = (text) => {
    if (!ttsEnabled || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang === 'hi' ? 'hi-IN' : lang === 'mr' ? 'mr-IN' : 'en-US';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    window.speechSynthesis.speak(utterance);
  };

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      setInput('Voice input not supported in this browser');
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = lang === 'hi' ? 'hi-IN' : lang === 'mr' ? 'mr-IN' : 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setInput(text);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const endpoint = lang !== 'en' ? `${API}/chatbot/voice` : `${API}/chatbot`;
      const payload = lang !== 'en'
        ? { message: text, session_id: sessionId, lang }
        : { message: text, session_id: sessionId };
      const res = await axios.post(endpoint, payload);
      const reply = res.data.reply;
      setMessages(prev => [...prev, { role: 'bot', text: reply }]);
      if (res.data.session_id) setSessionId(res.data.session_id);
      speak(reply);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: 'Sorry, try again!' }]);
    }
    setLoading(false);
  };

  const langLabels = { en: 'EN', hi: 'हि', mr: 'म' };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-[72px] sm:bottom-6 right-3 sm:right-4 z-50 w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-[#EF4444] to-[#8B5CF6] rounded-full flex items-center justify-center shadow-xl shadow-purple-500/30 hover:scale-110 transition-transform"
            data-testid="chatbot-toggle">
            <MessageCircle size={18} className="text-white sm:w-[22px] sm:h-[22px]" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.9 }}
            className="fixed bottom-2 right-2 sm:bottom-4 sm:right-4 z-50 w-[calc(100vw-16px)] sm:w-[340px] max-h-[75vh] sm:max-h-[500px] bg-[#0E0E1A] border border-[#8B5CF6]/20 rounded-2xl shadow-2xl shadow-purple-500/10 flex flex-col overflow-hidden"
            data-testid="chatbot-window">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#EF4444]/20 to-[#8B5CF6]/20 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between border-b border-[#8B5CF6]/10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-[#EF4444] to-[#8B5CF6] rounded-full flex items-center justify-center">
                  <Music size={12} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-xs sm:text-sm font-medium">Musicoul Assistant</p>
                  <p className="text-[#10B981] text-[9px] sm:text-[10px] flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#10B981] rounded-full" /> Online</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Language toggle */}
                <button onClick={() => setLang(l => l === 'en' ? 'hi' : l === 'hi' ? 'mr' : 'en')}
                  className="text-[10px] font-bold bg-[#8B5CF6]/15 text-[#A78BFA] px-2 py-1 rounded-lg" data-testid="chatbot-lang">
                  {langLabels[lang]}
                </button>
                {/* TTS toggle */}
                <button onClick={() => { setTtsEnabled(!ttsEnabled); window.speechSynthesis?.cancel(); }}
                  className="text-[#6B6B80] hover:text-white p-1" data-testid="chatbot-tts">
                  {ttsEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <button onClick={() => { setOpen(false); window.speechSynthesis?.cancel(); }} className="text-[#6B6B80] hover:text-white p-1" data-testid="chatbot-close"><X size={14} /></button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-2.5 sm:p-3 space-y-2.5 sm:space-y-3 min-h-[200px] max-h-[300px] sm:max-h-[350px]">
              {messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 sm:px-3.5 sm:py-2.5 rounded-2xl text-[12px] sm:text-[13px] leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-gradient-to-r from-[#EF4444]/20 to-[#8B5CF6]/20 text-white rounded-br-sm'
                      : 'bg-[#1A1A28] text-[#B0B0BE] rounded-bl-sm'
                  }`}>{m.text}</div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#1A1A28] px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5">
                    <span className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="border-t border-[#8B5CF6]/10 p-2 sm:p-3 flex gap-1.5 sm:gap-2">
              <button onClick={listening ? stopListening : startListening}
                className={`p-2 sm:p-2.5 rounded-xl flex-shrink-0 ${listening ? 'bg-[#EF4444]/20 text-[#EF4444] animate-pulse' : 'bg-[#1A1A28] text-[#6B6B80] hover:text-[#A78BFA]'}`}
                data-testid="chatbot-mic">
                {listening ? <MicOff size={14} /> : <Mic size={14} />}
              </button>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder={listening ? 'Listening...' : 'Ask about music...'}
                className="flex-1 bg-[#1A1A28] text-white px-3 py-2 sm:px-3.5 sm:py-2.5 text-[12px] sm:text-[13px] rounded-xl focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]/30 placeholder:text-[#505068]"
                data-testid="chatbot-input" />
              <button onClick={sendMessage} disabled={loading || !input.trim()}
                className="bg-gradient-to-r from-[#EF4444] to-[#8B5CF6] text-white p-2 sm:p-2.5 rounded-xl disabled:opacity-40"
                data-testid="chatbot-send"><Send size={14} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
